/* SKU/barcode substring lookup. Catalog identifiers only; product details are rendered live. */
(() => {
  const script = document.querySelector('script[data-number-index]');
  if (!script) return;
  const normalize = (value) => String(value).trim().replace(/^'+/, '').toLowerCase().replace(/[\s-]+/g, '');
  const queryKey = (value) => {
    const key = normalize(String(value).trim().replace(/^rpr[\s:#-]*(?=\d)/i, ''));
    return /\d/.test(key) && /^[a-z0-9./]+$/.test(key) ? key : '';
  };
  let indexPromise;
  const cache = new Map();
  async function request(url, signal) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 8000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      // Consume the body before clearing the timeout so slow responses also expire.
      const text = await response.text();
      return { ok: response.ok, status: response.status, text };
    } catch (error) {
      if (!signal?.aborted && error.name === 'AbortError') throw new Error('Request timed out');
      throw error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }
  async function matches(query) {
    const key = queryKey(query);
    if (!key) return [];
    if (!indexPromise) {
      indexPromise = request(script.dataset.numberIndex)
        .then((r) => { if (!r.ok) throw new Error('Index unavailable'); return JSON.parse(r.text); })
        .then((data) => data.products.map(([handle, codes]) => ({ handle, codes: codes.map(normalize) })))
        .catch((error) => { indexPromise = null; throw error; });
    }
    const products = await indexPromise;
    return products.map((p) => ({ ...p, rank: p.codes.includes(key) ? 0 : 1 }))
      .filter((p) => p.codes.some((code) => code.includes(key)))
      .sort((a, b) => a.rank - b.rank || a.handle.localeCompare(b.handle));
  }
  async function product(handle, signal) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (cache.has(handle)) return cache.get(handle);
    const root = window.Shopify?.routes?.root || '/';
    const response = await request(`${root}products/${encodeURIComponent(handle)}?section_id=rpr-number-product`, signal);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Product unavailable');
    const doc = new DOMParser().parseFromString(response.text, 'text/html');
    const data = doc.querySelector('[data-number-product]');
    if (!data) return null;
    const result = {
      codes: JSON.parse(data.dataset.numberCodes).map(normalize),
      suggestion: data.querySelector('template[data-number-suggestion]').innerHTML,
      card: data.querySelector('template[data-number-card]').innerHTML,
    };
    cache.set(handle, result);
    if (cache.size > 36) cache.delete(cache.keys().next().value);
    return result;
  }
  async function batch(entries, query, signal) {
    const key = queryKey(query);
    const output = new Array(entries.length);
    const failures = new Array(entries.length);
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(3, entries.length) }, async () => {
      while (cursor < entries.length) {
        const i = cursor++;
        try {
          const data = await product(entries[i].handle, signal);
          output[i] = data?.codes.some((code) => code.includes(key)) ? { ...data, handle: entries[i].handle } : null;
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          failures[i] = entries[i];
        }
      }
    }));
    return { products: output.filter(Boolean), failed: failures.filter(Boolean) };
  }
  async function enhance(query, markup, signal) {
    if (!queryKey(query)) return markup;
    try {
      const found = await matches(query);
      if (signal?.aborted || !found.length) return markup;
      const products = [];
      let offset = 0;
      while (products.length < 5 && offset < found.length) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const candidates = found.slice(offset, offset + 5 - products.length);
        const result = await batch(candidates, query, signal);
        products.push(...result.products);
        offset += candidates.length;
        // Don't walk the entire catalog during a service outage. Removed or
        // hidden products are not failures, so they don't stop replenishment.
        if (result.failed.length === candidates.length) break;
      }
      if (!products.length) return markup;
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const listbox = doc.querySelector('#predictive-search-results');
      if (!listbox) return markup;
      const group = doc.createElement('div');
      group.className = 'predictive-search__result-group';
      const heading = doc.createElement('h2');
      heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
      heading.id = 'number-search-heading';
      heading.textContent = 'Product number matches';
      const list = doc.createElement('ul');
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);
      products.forEach((p, i) => {
        const item = doc.createElement('li');
        item.id = `predictive-search-number-${i}`;
        item.className = 'predictive-search__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.innerHTML = p.suggestion;
        const url = item.querySelector('a').getAttribute('href').split('?')[0];
        doc.querySelectorAll('#predictive-search-results-products-list li').forEach((native) => {
          if (native.querySelector('a')?.getAttribute('href').split('?')[0] === url) native.remove();
        });
        list.append(item);
      });
      group.append(heading, list);
      listbox.prepend(group);
      const nativeList = doc.querySelector('#predictive-search-results-products-list');
      if (nativeList && !nativeList.children.length) nativeList.parentElement.remove();
      const count = doc.querySelector('[data-predictive-search-live-region-count-value]');
      if (count) count.textContent = `${products.length} product number matches shown. Submit search to see all matches.`;
      return doc.body.innerHTML;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      return markup; // Native search remains usable when the supplemental service fails.
    }
  }
  window.RPRNumberSearch = { matches, batch, enhance, queryKey };

  class NumberSearchResults extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.query = this.dataset.query;
      this.offset = 0;
      this.loaded = 0;
      this.failed = [];
      this.shownHandles = new Set();
      this.controller = new AbortController();
      this.status = this.querySelector('[data-number-status]');
      this.list = this.querySelector('ul');
      this.button = this.querySelector('button');
      this.retryButton = document.createElement('button');
      this.retryButton.type = 'button';
      this.retryButton.className = this.button.className;
      this.retryButton.dataset.numberRetry = '';
      this.retryButton.textContent = 'Retry failed matches';
      this.retryButton.hidden = true;
      this.button.after(this.retryButton);
      this.retryButton.addEventListener('click', () => this.loadMore(true));
      this.button.addEventListener('click', () => this.loadMore());
      if (!queryKey(this.query)) return;
      this.start();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.nativeObserver?.disconnect();
    }
    syncNativeResults() {
      this.nativeGrid?.querySelectorAll('[data-number-native-handle]').forEach((item) => {
        item.hidden = this.shownHandles.has(item.dataset.numberNativeHandle);
      });
    }
    async start() {
      try {
        this.entries = await matches(this.query);
        if (!this.entries.length || !this.isConnected) return;
        this.positions = new Map(this.entries.map((entry, i) => [entry.handle, i]));
        this.nativeGrid = this.closest('.template-search')?.querySelector('#ProductGridContainer');
        if (this.nativeGrid) {
          // Shopify replaces the grid after filtering or sorting. Reapply the
          // exclusions to new cards, without observing our own hidden changes.
          this.nativeObserver = new MutationObserver(() => this.syncNativeResults());
          this.nativeObserver.observe(this.nativeGrid, { childList: true, subtree: true });
        }
        this.hidden = false;
        await this.loadMore();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hidden = false;
          this.status.textContent = 'Product number search is temporarily unavailable. Standard search results are below.';
        }
      }
    }
    async loadMore(retry = false) {
      if (this.busy) return;
      this.busy = true;
      this.button.disabled = true;
      this.retryButton.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading product number matches…';
      try {
        const entries = retry ? this.failed.slice(0, 12) : this.entries.slice(this.offset, this.offset + 12);
        const { products, failed } = await batch(entries, this.query, this.controller.signal);
        if (!this.isConnected) return;
        for (const product of products) {
          const li = document.createElement('li');
          li.className = 'grid__item';
          li.dataset.numberPosition = this.positions.get(product.handle);
          li.innerHTML = product.card;
          this.list.append(li);
          this.shownHandles.add(product.handle);
        }
        if (retry) this.list.append(...Array.from(this.list.children).sort((a, b) => Number(a.dataset.numberPosition) - Number(b.dataset.numberPosition)));
        if (retry) this.failed = this.failed.slice(entries.length);
        else this.offset += entries.length;
        this.failed.push(...failed);
        this.loaded += products.length;
        if (this.loaded) {
          if (this.dataset.resultsTitle) document.title = this.dataset.resultsTitle;
          this.syncNativeResults();
          this.closest('.template-search')?.classList.remove('template-search--empty');
          this.closest('.template-search')?.querySelectorAll('[data-number-native-empty]').forEach((p) => { p.hidden = true; });
        }
        this.status.textContent = `${this.loaded} product number matches shown for “${this.query}”.`;
        if (this.failed.length) this.status.textContent += ` ${this.failed.length} matches could not be loaded. Please retry failed matches.`;
        if (!this.loaded && !this.failed.length && this.offset >= this.entries.length) this.hidden = true;
        this.button.textContent = 'Load more product number matches';
        this.button.hidden = this.offset >= this.entries.length;
        this.retryButton.hidden = !this.failed.length;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Some product number matches could not be loaded. Please retry.';
          this.button.textContent = 'Retry';
          this.button.hidden = false;
        }
      } finally {
        this.busy = false;
        this.button.disabled = false;
        this.retryButton.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('number-search-results', NumberSearchResults);
})();
